import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Button, Offcanvas } from "react-bootstrap";
import Switch from "react-switch";
import { MultiSelect } from "react-multi-select-component";
import { fetchSymbols } from "./Utilities/CoTraderUtils";
import {
  autoScrollSelectComponents,
} from "../Common/SelectMenuScroll";
import { formatCoTraderFilterLabel } from "./TickerUniverseOptions";
import EmLoader from "../Common/EmLoader";

const CoTraderMobileFiltersBar = ({
  selectedSymbol,
  prices,

  setSelectedSymbol,
  setUpdateSpeed,

  tenorList,
  setTenor, // pass null when not FX_FORWARD

  setInstrumentType,
  instrumentType,

  showSuggestions,
  setShowSuggestions,

  language,
  setLanguage,
  languageList,
  setLanguageKey,

  isDark,
}) => {
  const [showFilterMenu, setShowFilterMenu] = useState(false);

  const [symbols, setSymbols] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedSymbol1, setSelectedSymbol1] = useState({
    value: localStorage.getItem("default_symbol") || "USDJPY",
    label: localStorage.getItem("default_symbol") || "USDJPY",
  });

  const [selectedUpdateSpeed, setSelectedUpdateSpeed] = useState({
    value: 2,
    label: "2s",
  });
  const updateSpeed = useMemo(
    () => [
      { value: 2, label: "2s" },
      { value: 3, label: "3s" },
      { value: 5, label: "5s" },
    ],
    [],
  );
  const instrumentOptions = useMemo(
    () => [
      { value: "FX_SPOT", label: "FX SPOT" },
      { value: "FX_FORWARD", label: "FX FORWARD" },
    ],
    [],
  );

  const tenorType = useMemo(() => {
    return (tenorList || []).map((item) => ({ value: item, label: item }));
  }, [tenorList]);

  const [selectedTenor, setSelectedTenor] = useState(
    tenorType.map((item) => item)
  );
  const selectedInstrumentOption = useMemo(
    () => ({
      value: instrumentType,
      label: formatCoTraderFilterLabel(instrumentType || "FX_SPOT"),
    }),
    [instrumentType],
  );
  const languageOptions = useMemo(
    () => languageList.map((lang) => ({ value: lang, label: lang })),
    [languageList],
  );
  const selectedLanguageOption = useMemo(
    () => languageOptions.find((option) => option.value === language) || null,
    [language, languageOptions],
  );
  useEffect(() => {
    fetchSymbols({ setSymbols, setIsLoading });
  }, []);

  useEffect(() => {
    if (setTenor) {
      const all = tenorType.map((item) => item);
      setSelectedTenor(all);
      setTenor(all.map((x) => x.value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenorList]);

  const setSymbol = (symbol) => {
    setSelectedSymbol1(symbol);
    setSelectedSymbol(symbol.value);
    localStorage.setItem("default_symbol", symbol.value);
  };

  const handleUpdateSpeed = (speed) => {
    setSelectedUpdateSpeed(speed);
    setUpdateSpeed(speed.value);
  };

  const handleTenor = (tenor) => {
    setTenor(tenor.map((item) => item.value));
    setSelectedTenor(tenor);
  };

  const customValueRenderer = (selected, _options) => {
    if (selected.length === _options.length) return "All Tenors";
    if (selected.length === 0) {
      setTenor([tenorType[0].value]);
      setSelectedTenor([tenorType[0]]);
      return selected.map(({ label }) => label);
    }
    if (selected.length === 1) return selected.map(({ label }) => label);
    return selected.map(({ label }) => label).join(", ");
  };

  // ====== THEME TOKENS ======
  const t = useMemo(() => {
    if (isDark) {
      return {
        pageBg: "#0B0F1A",
        surface: "#12172A",
        surface2: "#0F1424",
        border: "#232B4A",
        text: "#E8ECF8",
        muted: "#A6B0CF",
        primary: "#4C7DFF",
        primarySoft: "rgba(76,125,255,0.14)",
        btnOutlineBorder: "#2B3560",
        selectedBg: "#4C7DFF",
        selectedText: "#FFFFFF",
        menuShadow: "0 18px 40px rgba(0,0,0,0.45)",
      };
    }
    return {
      pageBg: "#FFFFFF",
      surface: "#FFFFFF",
      surface2: "#FFFFFF",
      border: "#E9ECEF",
      text: "#0B1220",
      muted: "#5B6472",
      primary: "#0D6EFD",
      primarySoft: "rgba(13,110,253,0.10)",
      btnOutlineBorder: "#0D6EFD",
      selectedBg: "#0D6EFD",
      selectedText: "#FFFFFF",
      menuShadow: "0 14px 30px rgba(11,18,32,0.12)",
    };
  }, [isDark]);

  const selectPortalStyles = useMemo(
    () => ({
      menuPortal: (base) => ({ ...base, zIndex: 99999 }),
      container: (base) => ({ ...base, color: t.text }),
      control: (base, state) => ({
        ...base,
        minHeight: "46px",
        borderRadius: "12px",
        backgroundColor: t.surface2,
        borderColor: state.isFocused ? t.primary : t.border,
        boxShadow: state.isFocused ? `0 0 0 3px ${t.primarySoft}` : "none",
        ":hover": { borderColor: state.isFocused ? t.primary : t.border },
      }),
      valueContainer: (base) => ({ ...base, color: t.text }),
      singleValue: (base) => ({ ...base, color: t.text }),
      placeholder: (base) => ({ ...base, color: t.muted }),
      input: (base) => ({ ...base, color: t.text }),
      menu: (base) => ({
        ...base,
        zIndex: 99999,
        backgroundColor: t.surface,
        border: `1px solid ${t.border}`,
        borderRadius: 14,
        overflow: "hidden",
        boxShadow: t.menuShadow,
      }),
      menuList: (base) => ({
        ...base,
        backgroundColor: t.surface,
        padding: 6,
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? t.selectedBg
          : state.isFocused
          ? isDark
            ? "rgba(35,43,74,0.65)"
            : "rgba(13,110,253,0.08)"
          : t.surface,
        color: state.isSelected ? t.selectedText : t.text,
        borderRadius: 10,
        cursor: "pointer",
        ":active": {
          backgroundColor: state.isSelected ? t.selectedBg : t.primarySoft,
        },
      }),
      indicatorSeparator: (base) => ({ ...base, backgroundColor: t.border }),
      dropdownIndicator: (base) => ({ ...base, color: t.muted }),
      clearIndicator: (base) => ({ ...base, color: t.muted }),
      noOptionsMessage: (base) => ({ ...base, color: t.muted }),
      loadingMessage: (base) => ({ ...base, color: t.muted }),
    }),
    [t, isDark]
  );

  return (
    <>
      {/* SYMBOL + PRICE + SINGLE ACTION BUTTON */}
      <div
        className="px-3 pt-2 pb-2"
        style={{
          borderBottom: `1px solid ${t.border}`,
          background: t.surface,
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <div
              className="fw-semibold"
              style={{ fontSize: "1.05rem", color: t.text }}
            >
              {selectedSymbol}
            </div>
            <div style={{ fontSize: "0.85rem", color: t.muted }}>
              {prices?.length > 0 ? `${prices?.at(-1)?.current_price}` : "--"}
            </div>
          </div>

          <Button
            className="px-3 rounded-pill"
            onClick={() => setShowFilterMenu(true)}
            disabled={isLoading}
            style={{
              background: "transparent",
              border: `1px solid ${t.btnOutlineBorder}`,
              color: t.primary,
            }}
          >
            <i className="fa-solid fa-sliders me-2" />
            Filters
          </Button>
        </div>
      </div>

      {/* SINGLE OFFCANVAS: Filters + Settings */}
      <Offcanvas
        show={showFilterMenu}
        onHide={() => setShowFilterMenu(false)}
        placement="bottom"
        style={{
          height: "85%",
          background: t.pageBg,
          borderTop: `1px solid ${t.border}`,
          paddingBottom: "60px",
        }}
      >
        <Offcanvas.Header
          closeButton
          style={{
            background: t.surface,
            borderBottom: `1px solid ${t.border}`,
          }}
        >
          <Offcanvas.Title style={{ color: t.text }}>
            Filters & Settings
          </Offcanvas.Title>

          {/* make close icon visible in dark */}
          <style>{`
            .offcanvas-header .btn-close { filter: ${isDark ? "invert(1)" : "none"}; }
          `}</style>
        </Offcanvas.Header>

        <Offcanvas.Body style={{ background: t.pageBg, color: t.text }}>
          {/* Loading */}
          {isLoading && (
            <div className="d-flex align-items-center gap-2 mb-3">
              <EmLoader size="sm" />
              <span style={{ color: t.muted }}>Loading symbols...</span>
            </div>
          )}

          {/* ========= FILTERS ========= */}
          <div className="mb-2 fw-semibold" style={{ color: t.muted }}>
            Filters
          </div>

          {/* Instrument */}
          <div className="mb-3">
            <div style={{ color: t.muted, fontSize: "0.85rem" }} className="mb-1">
              Instrument
            </div>
            <Select
              options={instrumentOptions}
              value={selectedInstrumentOption}
              onChange={(x) => setInstrumentType(x.value)}
              isDisabled={isLoading}
              menuPortalTarget={document.body}
              styles={selectPortalStyles}
              components={autoScrollSelectComponents}
            />
          </div>

          {/* Symbol */}
          <div className="mb-3">
            <div style={{ color: t.muted, fontSize: "0.85rem" }} className="mb-1">
              Symbol
            </div>
            <Select
              options={symbols}
              value={selectedSymbol1}
              onChange={(x) => setSymbol(x)}
              isDisabled={isLoading}
              menuPortalTarget={document.body}
              styles={selectPortalStyles}
              components={autoScrollSelectComponents}
            />
          </div>

          {/* Update Speed */}
          <div className="mb-3">
            <div style={{ color: t.muted, fontSize: "0.85rem" }} className="mb-1">
              Update speed
            </div>
            <Select
              options={updateSpeed}
              value={selectedUpdateSpeed}
              onChange={(x) => handleUpdateSpeed(x)}
              menuPortalTarget={document.body}
              styles={selectPortalStyles}
              components={autoScrollSelectComponents}
            />
          </div>

          {/* Tenor only when FX_FORWARD */}
          {setTenor && (
            <div className="mb-3">
              <div style={{ color: t.muted, fontSize: "0.85rem" }} className="mb-1">
                Tenor
              </div>

              {/* MultiSelect doesn't theme nicely by default: wrap in a themed container */}
              <div
                style={{
                  background: t.surface2,
                  border: `1px solid ${t.border}`,
                  borderRadius: 12,
                  padding: 6,
                }}
              >
                <MultiSelect
                  options={tenorType}
                  value={selectedTenor || []}
                  className="multi-select"
                  valueRenderer={customValueRenderer}
                  onChange={handleTenor}
                />
              </div>

              {/* optional: improve MultiSelect dark styling */}
              <style>{`
                .multi-select * { color: ${t.text}; }
                .multi-select .dropdown-container {
                  background: transparent !important;
                  border: none !important;
                }
                .multi-select .dropdown-heading {
                  background: transparent !important;
                  color: ${t.text} !important;
                }
                .multi-select .dropdown-heading-value,
                .multi-select .dropdown-heading-dropdown-arrow,
                .multi-select .clear-selected-button {
                  color: ${t.text} !important;
                }
                .multi-select .dropdown-content {
                  background: ${t.surface} !important;
                  border: 1px solid ${t.border} !important;
                  border-radius: 12px !important;
                  box-shadow: ${t.menuShadow} !important;
                }
                .multi-select .panel-content,
                .multi-select .select-panel {
                  background: ${t.surface} !important;
                }
                .multi-select .search {
                  background: ${t.surface2} !important;
                  border-bottom: 1px solid ${t.border} !important;
                }
                .multi-select .search input {
                  background: ${t.surface2} !important;
                  color: ${t.text} !important;
                }
                .multi-select .select-item:hover {
                  background: ${isDark ? "rgba(35,43,74,0.45)" : "rgba(13,110,253,0.08)"} !important;
                }
                .multi-select .selected-item {
                  background: ${t.primarySoft} !important;
                  border: 1px solid ${t.border} !important;
                  color: ${t.text} !important;
                }
              `}</style>
            </div>
          )}

          <hr className="my-4" style={{ borderColor: t.border }} />

          {/* ========= SETTINGS ========= */}
          <div className="mb-2 fw-semibold" style={{ color: t.muted }}>
            Settings
          </div>

          {/* Show suggestions */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span className="d-flex align-items-center" style={{ color: t.text }}>
              <i className="fa fa-lightbulb me-2" />
              Show Suggestions
            </span>
            <Switch
              onChange={() => setShowSuggestions(!showSuggestions)}
              checked={showSuggestions}
              height={20}
              width={40}
              offColor={isDark ? "#232B4A" : "#E9ECEF"}
              onColor={t.primary}
              uncheckedIcon={false}
              checkedIcon={false}
            />
          </div>

          {/* Language */}
          <div className="d-flex align-items-center w-100 mb-3">
            <div className="me-2" style={{ color: t.text }}>
              Language:
            </div>
            <div className="flex-grow-1">
              <Select
                options={languageOptions}
                value={selectedLanguageOption}
                onChange={(option) => {
                  const lang = option?.value || "English";
                  localStorage.setItem("user_language", lang);
                  setLanguage(lang);
                  setLanguageKey((prev) => prev + 1);
                  setShowFilterMenu(false);
                }}
                menuPortalTarget={document.body}
                styles={selectPortalStyles}
                components={autoScrollSelectComponents}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="d-grid gap-2 mt-4">
            <button
              className="btn rounded-pill"
              onClick={() => setShowFilterMenu(false)}
              style={{
                background: t.primary,
                border: "none",
                color: "#fff",
              }}
            >
              Done
            </button>
            <button
              className="btn rounded-pill"
              onClick={() => setShowFilterMenu(false)}
              style={{
                background: isDark ? t.surface2 : "#F8F9FA",
                border: `1px solid ${t.border}`,
                color: t.text,
              }}
            >
              Close
            </button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default CoTraderMobileFiltersBar;
